import { displayNetwork, ruleKind, ruleValue } from "@cg/protocol/rules";
import type { DecisionEvent } from "@cg/protocol";
import type { Install } from "./api.ts";
import { isTorList, sourceLabel, typesText } from "./sources.ts";

const nf = new Intl.NumberFormat("en-US");
export const num = (n: number) => nf.format(n);
export const pct = (n: number | null, digits = 1) => (n === null || !Number.isFinite(n) ? "–" : `${(n * 100).toFixed(digits)}%`);
export const ms = (n: number | null) => (n === null ? "–" : n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${n} ms`);

export function ago(t: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86_400)} d ago`;
}

export const clock = (t: number) => new Date(t).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
export const day = (t: number) => new Date(t).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
export const dayKey = (t: number) => new Date(t).toDateString();

/** "0.5.2-SNAPSHOT" ≥ "0.5.2": development builds already carry the features of their version. */
export function versionAtLeast(version: string | null | undefined, min: string): boolean {
  const parts = (x: string | null | undefined) => ((x ?? "").match(/\d+/g) ?? []).slice(0, 3).map(Number);
  const a = parts(version), b = parts(min);
  for (let i = 0; i < 3; i++) if ((a[i] ?? 0) !== (b[i] ?? 0)) return (a[i] ?? 0) > (b[i] ?? 0);
  return true;
}

export const platformName = { BUKKIT: "Paper / Spigot", BUNGEE: "BungeeCord", VELOCITY: "Velocity" } as const;
export const serverName = (i: Pick<Install, "name" | "platform" | "id">) => i.name ?? `${platformName[i.platform]} ${i.id.slice(4, 8)}`;

const regionNames = typeof Intl.DisplayNames === "function" ? new Intl.DisplayNames(["en"], { type: "region" }) : null;
export const countryName = (cc: string) => { try { return regionNames?.of(cc) ?? cc; } catch { return cc; } };

/** Loopback and private ranges cannot be looked up by any provider (a test from the same machine or LAN). */
export function isPrivateIp(ip: string): boolean {
  const v = ip.toLowerCase();
  if (v === "::1" || v.startsWith("fc") || v.startsWith("fd") || v.startsWith("fe80:")) return true;
  const m = /^(\d+)\.(\d+)\./.exec(v);
  if (!m) return false;
  const a = Number(m[1]), b = Number(m[2]);
  return a === 127 || a === 10 || (a === 192 && b === 168) || (a === 172 && b >= 16 && b <= 31) || (a === 100 && b >= 64 && b <= 127);
}

/** "Would refuse": logged in OBSERVE mode with a flag that ENFORCE would act on. */
export const wouldRefuse = (e: DecisionEvent) => e.mode === "OBSERVE" && e.outcome === "ALLOW" && e.flags.length > 0;

export type Verdict = "admitted" | "refused" | "would-refuse" | "error";
export function verdict(e: DecisionEvent): Verdict {
  if (e.outcome === "DENY") return "refused";
  if (e.outcome === "ERROR") return "error";
  return wouldRefuse(e) ? "would-refuse" : "admitted";
}

export const reasonText: Record<DecisionEvent["reason"], string> = {
  CHECKS_COMPLETE: "All checks passed",
  FLAG_ALLOWED: "Flagged, let in anyway",
  UNKNOWN_ALLOWED: "Inconclusive, let in by failure policy",
  ACCESS_RULE: "Manual access rule",
  LOOKUP_UNAVAILABLE: "Lookups unavailable, failure policy applied",
  OVERLOAD: "Overload protection",
  VPN_FLAG: "VPN or proxy",
  GEO_FLAG: "Country not allowed",
  INTERNAL_ERROR: "Internal error",
  IDENTITY_UNAVAILABLE: "Identity could not be verified",
  EXTERNAL_POLICY: "Refused by another plugin",
  EXTERNAL_UNAVAILABLE: "Another plugin's check unavailable",
};

const flagText = { VPN: "a VPN or proxy", GEO: "a blocked country", ACCESS_POLICY: "an access rule",
  EXTERNAL_POLICY: "a refusal by another plugin" } as const;

/** Reason label for one entry: flagged-but-admitted reads differently in pencil (OBSERVE) and ink (ENFORCE). */
export const reasonLabel = (e: DecisionEvent) => (wouldRefuse(e) ? "Would refuse in ENFORCE" : reasonText[e.reason]);

/** One plain-English sentence for the "why" panel. */
export function explain(e: DecisionEvent): string {
  const positiveSources = e.sources.filter((s) => s.status === "POSITIVE" && s.voting);
  const positives = [...new Set(positiveSources.map((s) => sourceLabel(s.id)))];
  const country = e.sources.find((s) => s.country)?.country;
  const flags = e.flags.map((f) => flagText[f]).join(" and ");
  switch (verdict(e)) {
    case "refused":
      if (e.reason === "ACCESS_RULE") return "A manual deny rule matched this connection, so it was refused before any lookup.";
      if (e.reason === "VPN_FLAG" && positiveSources.some((s) => isTorList(s.id))) return "Refused: the address is on the Tor exit list the server keeps itself, so no service had to be asked.";
      if (e.reason === "VPN_FLAG") {
        // 0.5.2+ sources say what they found (Intel: which list), and when their data is from.
        const found = typesText([...new Set(positiveSources.flatMap((s) => s.types ?? []))].filter((t) => t !== "HOSTING"));
        const asOf = positiveSources.find((s) => s.data_as_of)?.data_as_of;
        return `Refused: ${positives.length ? positives.join(" and ") : "the providers"} ${found ? `listed the address as a ${found}` : "reported a VPN or proxy"}${country ? ` in ${countryName(country)}` : ""}${asOf ? ` (data from ${day(asOf)})` : ""}.`;
      }
      if (e.reason === "GEO_FLAG") return `Refused: your country rules do not allow connections from ${country ? countryName(country) : "this country"}.`;
      if (e.reason === "LOOKUP_UNAVAILABLE") return "Refused because no provider answered in time and the failure policy is CLOSED.";
      if (e.reason === "OVERLOAD") return "Refused by overload protection: too many logins arrived at once.";
      if (e.reason === "EXTERNAL_POLICY") return "Refused because another plugin on this server (for example its ban list) said no. Connection Guard did not run its own lookups for it.";
      if (e.reason === "EXTERNAL_UNAVAILABLE") return "Refused for now because another plugin's check did not answer and its failure policy is CLOSED. The player can try again.";
      return `Refused: ${reasonText[e.reason].toLowerCase()}.`;
    case "would-refuse":
      return `Connection Guard found ${flags} but let the player in, because this server only observes. In ENFORCE mode this connection would have been refused.`;
    case "error":
      return "The check failed with an internal error; the connection was handled by your failure policy.";
    default:
      if (e.reason === "ACCESS_RULE") return "A manual allow or exempt rule matched, so no lookup was needed.";
      if (e.reason === "EXTERNAL_UNAVAILABLE") return "Another plugin's check did not answer; its failure policy lets such players in.";
      if (e.reason === "UNKNOWN_ALLOWED") return "The providers could not give an answer in time; your failure policy lets such players in.";
      if (e.reason === "FLAG_ALLOWED") return `Connection Guard found ${flags}, and your settings let such players in.`;
      if (e.sources.some((s) => s.types?.includes("RELAY") && s.status !== "POSITIVE")) {
        return "The address belongs to a privacy relay such as iCloud Private Relay. Your settings let relays in; the checks found nothing else.";
      }
      return "Every check came back clean.";
  }
}

/**
 * What to do about a change a server refused, for messages that need more than the plugin's own words. Plugins from
 * 0.5.2 can hold a locally activated policy version; while it is active, the dashboard cannot change decision settings.
 */
export function configErrorHint(message: string | null | undefined): { text: string; command?: string } | null {
  const m = (message ?? "").toLowerCase();
  if (m.includes("local policy owns") || m.includes("local policy and dashboard")) {
    return { text: "A policy version was activated on the server itself. Until it is released there, it owns the protection mode, failure policies, kick switches and country rules; services, actions and exemptions can still be changed here. To manage everything from the dashboard again, run this on the server and then /cg policy release with the token it shows.", command: "/cg policy status" };
  }
  if (m.includes("cannot be set from the dashboard")) {
    return { text: "This server's Connection Guard version does not accept one of these settings from the dashboard. Update the plugin, or change it in config.yml." };
  }
  return null;
}

/** "in 47 min", "in 23 h", "until 11 Oct": when a time-limited rule ends. */
export function until(t: number, now = Date.now()): string {
  const min = Math.max(1, Math.round((t - now) / 60_000));
  if (min < 60) return `in ${min} min`;
  if (min < 48 * 60) return `in ${Math.round(min / 60)} h`;
  return `until ${new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
}

/** Durations offered for time-limited rules, in minutes (null: permanent). */
export const RULE_DURATIONS: { minutes: number | null; label: string; long: string }[] = [
  { minutes: 60, label: "1 h", long: "for 1 hour" },
  { minutes: 24 * 60, label: "24 h", long: "for 24 hours" },
  { minutes: 7 * 24 * 60, label: "7 days", long: "for 7 days" },
  { minutes: null, label: "Always", long: "permanently" },
];

/** Servers that enforce rule expiry themselves (protocol capability "rule_expiry"). */
export const supportsExpiry = (i: { status: { capabilities?: string[] } | null }) => Boolean(i.status?.capabilities?.includes("rule_expiry"));

/** A rule target in words: "Tor connections", "Provider: Hetzner Online GmbH", "AS3320", "203.0.113.0/24". */
export function describeTarget(target: string, names: Map<string, string | null>): { text: string; kind: string } {
  const kind = ruleKind(target);
  const value = ruleValue(target);
  switch (kind) {
    case "player": return { text: names.get(target) ?? `Player ${target.slice(0, 8)}…`, kind: "Player" };
    case "asn": return { text: `AS${value}`, kind: "Network" };
    case "isp": return { text: value, kind: "Provider" };
    case "operator": return { text: value, kind: "Operator" };
    case "country": return { text: countryName(value), kind: "Country" };
    case "type": return { text: TYPE_LABEL[value] ?? value, kind: "Connection type" };
    case "range": return { text: displayNetwork(target), kind: "Address range" };
    default: return { text: displayNetwork(target), kind: "Address" };
  }
}
const TYPE_LABEL: Record<string, string> = { VPN: "VPN", PROXY: "Proxy", TOR: "Tor", RELAY: "Privacy relay", HOSTING: "Hosting and data centres" };
